// Formatação do Dashboard de Vendas.
//
// Só funções puras: rótulo, cor e texto de número. É o que a tela usa para
// desenhar e o que os testes cobrem — nenhum gráfico precisa subir para conferir
// se "credit_card" vira "Cartão de crédito" ou se 0,681 vira "68%".
//
// As cores saem da paleta do admin, em tons escuros o bastante para o texto ao
// lado ser legível sobre o cartão branco. Meio de pagamento e status usam
// conjuntos SEPARADOS de propósito: verde é "pago" e nunca um meio de pagamento,
// para as duas leituras não se confundirem no mesmo painel.

const currencyFormatter = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const compactFormatter = new Intl.NumberFormat("pt-BR", {
  maximumFractionDigits: 1,
});

const integerFormatter = new Intl.NumberFormat("pt-BR");

const toNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * R$ 184.320,00 — o valor exato, para tabela e tooltip.
 *
 * O Intl separa o "R$" com espaço NÃO separável (U+00A0), escrito aqui pelo
 * escape para não virar um espaço comum na primeira formatação do arquivo. Ele
 * vira espaço normal para o texto poder quebrar e para a comparação em teste
 * não depender de um caractere invisível.
 */
export function formatCurrency(value) {
  return currencyFormatter.format(toNumber(value)).replace(/ /g, " ");
}

/**
 * R$ 184,3 mil — o valor curto, para o número grande do topo e o eixo.
 *
 * Abaixo de mil não abrevia: "R$ 0,9 mil" é pior de ler do que "R$ 900".
 */
export function formatCurrencyShort(value) {
  const number = toNumber(value);
  const absolute = Math.abs(number);

  if (absolute >= 1000000) {
    return `R$ ${compactFormatter.format(number / 1000000)} mi`;
  }
  if (absolute >= 1000) {
    return `R$ ${compactFormatter.format(number / 1000)} mil`;
  }
  return `R$ ${compactFormatter.format(number)}`;
}

export function formatInteger(value) {
  return integerFormatter.format(toNumber(value));
}

/** A API devolve proporção (0,681); a tela mostra porcentagem inteira. */
export function formatPercent(rate) {
  if (rate === null || rate === undefined) return "—";
  return `${Math.round(toNumber(rate) * 100)}%`;
}

/** "2026-04-18" vira "18/04" — o eixo do gráfico não tem largura para o ano. */
export function formatDayShort(date) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(date ?? ""));
  if (!match) return String(date ?? "");
  return `${match[3]}/${match[2]}`;
}

const PAYMENT_LABELS = {
  pix: "Pix",
  credit_card: "Cartão de crédito",
  boleto: "Boleto",
  free: "Gratuito",
};

const PAYMENT_COLORS = {
  pix: "#556ee6",
  credit_card: "#7b3294",
  boleto: "#c2410c",
  free: "#64748b",
};

const STATUS_LABELS = {
  paid: "Pagas",
  pending: "Pendentes",
  expired: "Expiradas",
  canceled: "Canceladas",
  refunded: "Reembolsadas",
};

const STATUS_COLORS = {
  paid: "#157347",
  pending: "#b0700a",
  expired: "#94a3b8",
  canceled: "#94a3b8",
  refunded: "#b42318",
};

// Ordem do funil: do que deu certo ao que deu errado. Um status que a API
// devolva e não esteja aqui vai para o fim, com o nome cru — melhor mostrar
// "chargeback" sem tradução do que escondê-lo do total.
const STATUS_ORDER = ["paid", "pending", "expired", "canceled", "refunded"];

export function paymentTypeLabel(type) {
  return PAYMENT_LABELS[type] ?? type ?? "Não informado";
}

export function paymentTypeColor(type) {
  return PAYMENT_COLORS[type] ?? "#64748b";
}

export function statusLabel(status) {
  return STATUS_LABELS[status] ?? status ?? "Não informado";
}

export function statusColor(status) {
  return STATUS_COLORS[status] ?? "#94a3b8";
}

export function sortStatus(rows = []) {
  return [...rows].sort((a, b) => {
    const indexA = STATUS_ORDER.indexOf(a.status);
    const indexB = STATUS_ORDER.indexOf(b.status);
    return (indexA === -1 ? STATUS_ORDER.length : indexA) - (indexB === -1 ? STATUS_ORDER.length : indexB);
  });
}

/** 1x, 6x — no cartão, a parcela é o rótulo que o financeiro procura. */
export function installmentLabel(installments) {
  const parsed = toNumber(installments);
  return `${parsed > 0 ? parsed : 1}x`;
}

/**
 * A barra de ocupação do produto.
 *
 * Produto ilimitado (`available: null`) não tem proporção: devolve `null`, e a
 * tabela mostra um traço no lugar da barra.
 */
export function occupancyPercent(product) {
  if (!product || product.available === null || product.available === undefined) return null;
  const available = toNumber(product.available);
  if (available <= 0) return null;
  return Math.min(100, Math.round((toNumber(product.sold) / available) * 100));
}
